import type { Task } from '@portfolio-pm/shared';
import { parseSmartAdd } from '@portfolio-pm/smart-add';

export type WorkspaceSearchMode = 'empty' | 'search' | 'command';

export interface ParsedWorkspaceSearch {
  mode: WorkspaceSearchMode;
  raw: string;
  commandQuery?: string;
  terms: string[];
  filters: SearchFilter[];
}

export type SearchFilter =
  | { kind: 'due'; value: string }
  | { kind: 'scheduled'; value: string }
  | { kind: 'date'; value: string }
  | { kind: 'priority'; value: string }
  | { kind: 'tag'; value: string }
  | { kind: 'project'; value: string }
  | { kind: 'owner'; value: string }
  | { kind: 'estimate'; value: string }
  | { kind: 'location'; value: string }
  | { kind: 'energy'; value: string };

const FILTER_KEYS: Record<string, SearchFilter['kind']> = {
  due: 'due',
  scheduled: 'scheduled',
  schedule: 'scheduled',
  start: 'scheduled',
  date: 'date',
  priority: 'priority',
  tag: 'tag',
  tags: 'tag',
  project: 'project',
  list: 'project',
  owner: 'owner',
  estimate: 'estimate',
  location: 'location',
  energy: 'energy',
};

export function parseWorkspaceSearch(input: string): ParsedWorkspaceSearch {
  const raw = input.trim();
  if (!raw) return { mode: 'empty', raw: input, terms: [], filters: [] };
  if (raw.startsWith('/')) {
    return {
      mode: 'command',
      raw: input,
      commandQuery: raw.replace(/^\/+/, '').trim().toLowerCase(),
      terms: [],
      filters: [],
    };
  }

  const terms: string[] = [];
  const filters: SearchFilter[] = [];

  for (const token of raw.split(/\s+/)) {
    if (!token) continue;
    const shorthand = parseShorthandFilter(token);
    if (shorthand) {
      filters.push(shorthand);
      continue;
    }

    const keyword = parseKeywordFilter(token);
    if (keyword) {
      filters.push(keyword);
      continue;
    }

    terms.push(token);
  }

  return { mode: 'search', raw: input, terms, filters };
}

export function matchesTaskSearch(task: Task, query: string, now = Date.now()): boolean {
  const parsed = parseWorkspaceSearch(query);
  if (parsed.mode !== 'search') return false;
  if (task.trashedAt !== undefined) return false;

  const matchesTerms =
    parsed.terms.length === 0 ||
    parsed.terms.every((term) => includesText(task.title, term) || includesText(task.notes, term));

  return matchesTerms && parsed.filters.every((filter) => matchesTaskFilter(task, filter, now));
}

export function hasSearchCriteria(query: string): boolean {
  const parsed = parseWorkspaceSearch(query);
  return parsed.mode === 'search' && (parsed.terms.length > 0 || parsed.filters.length > 0);
}

function parseShorthandFilter(token: string): SearchFilter | undefined {
  const operator = token[0];
  if (operator !== '^' && operator !== '~' && operator !== '&') return undefined;
  const value = token.slice(1).trim();
  if (!value) return undefined;
  if (operator === '^') return { kind: 'due', value };
  if (operator === '~') return { kind: 'scheduled', value };
  return { kind: 'date', value };
}

function parseKeywordFilter(token: string): SearchFilter | undefined {
  const colon = token.indexOf(':');
  if (colon <= 0) return undefined;
  const key = token.slice(0, colon).toLowerCase();
  const value = token.slice(colon + 1);
  const kind = FILTER_KEYS[key];
  if (!kind || !value) return undefined;
  return { kind, value };
}

function matchesTaskFilter(task: Task, filter: SearchFilter, now: number): boolean {
  switch (filter.kind) {
    case 'due':
      return matchesDateText(task.dueAt, filter.value, now);
    case 'scheduled':
      return matchesDateText(task.scheduledAt, filter.value, now);
    case 'date':
      return (
        matchesDateText(task.dueAt, filter.value, now) ||
        matchesDateText(task.scheduledAt, filter.value, now)
      );
    case 'priority':
      return task.priority === Number(filter.value.replace(/^p/i, ''));
    case 'tag':
      return task.tags.some((tag) => equalsLoose(tag, filter.value.replace(/^#/, '')));
    case 'project':
      return equalsLoose(task.projectId, filter.value);
    case 'owner':
      return includesText(task.owner, filter.value);
    case 'estimate':
      return task.estimateMinutes === Number(filter.value);
    case 'location':
      return includesText(task.location, filter.value);
    case 'energy':
      return task.energy === filter.value.toLowerCase();
  }
}

function matchesDateText(timestamp: number | undefined, value: string, now: number): boolean {
  if (timestamp === undefined) return false;
  const target = resolveDate(value, now);
  return target !== undefined && startOfDayLocal(timestamp) === startOfDayLocal(target);
}

function resolveDate(value: string, now: number): number | undefined {
  const parsed = parseSmartAdd(`Search ^${value}`, { now: new Date(now) });
  if ('error' in parsed) return undefined;
  return parsed.dueAt;
}

function startOfDayLocal(timestamp: number): number {
  const date = new Date(timestamp);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function includesText(value: string | undefined, query: string): boolean {
  return value?.toLowerCase().includes(query.toLowerCase()) ?? false;
}

function equalsLoose(left: string | undefined, right: string): boolean {
  return left?.toLowerCase() === right.toLowerCase();
}
